'use client';
import {
  Avatar,
  AvatarImage,
  AvatarFallback,
} from '@ahafood/ui/components/avatar';
import { Button } from '@ahafood/ui/components/button';
import { assetPrefix } from '@ahm/api-wrappers-core';
import { RotateCcw, XIcon } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { LoginDialog } from '../(main)/store/components/fb-login/login-dialog';
import Chat from '../chat/page';
import { useNavigation } from '@/components/navigation/navigation-context';
import { useBottomPosition } from '@/hooks/use-bottom-position';
import { useChatThread } from '@/hooks/use-chat-thread';
import { trackEvent } from '@/lib/event-tracking';
import { useAuthStore } from '@/store';

type AmoriContainerProps = {
  isOpen: boolean;
  setOpenChange: (isOpen: boolean) => void;
};

export default function AmoriContainer({
  isOpen,
  setOpenChange,
}: AmoriContainerProps) {
  const { user, isAuthenticated, isLoading } = useAuthStore();
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const { isNavigationVisible } = useNavigation();
  const { threadId, restartThread } = useChatThread(
    user?.cunstomerId ?? null,
    null
  );
  if (typeof window !== 'undefined') {
    window.setOpenChange = setOpenChange;
  }
  const { bottomPosition } = useBottomPosition();

  useEffect(() => {
    if (isAuthenticated) {
      setShowLoginDialog(false);
    }
  }, [isAuthenticated]);

  // Prevent body scroll when container is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup function to restore scroll when component unmounts
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const UnAuthen = () => (
    <div className="flex flex-col items-center justify-center h-full gap-4 bg-white">
      <Avatar className="size-40">
        <AvatarImage
          src={`${assetPrefix}/static/images/robot-cook.webp`}
          alt="AI Bot"
        />
        <AvatarFallback className="rounded-none">AI Bot</AvatarFallback>
      </Avatar>
      <div className="text-xl font-bold text-blue-60">
        Đăng nhập để chat với trợ lý AI
      </div>
      <Button
        size="lg"
        className="bg-blue-60 text-white rounded-full px-6 py-2 font-bold"
        onClick={() => {
          setShowLoginDialog(true);
        }}
      >
        Đăng nhập
      </Button>
    </div>
  );

  return (
    <>
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50 z-[9990] pointer-events-auto"
            onClick={() => setOpenChange(false)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setOpenChange(false);
            }}
            role="button"
            tabIndex={0}
            aria-label="Close Amori chat"
          />
          {/* Content */}
          <div
            className="fixed inset-x-0 z-[9995] bg-white overflow-hidden pointer-events-auto"
            style={{
              height: isNavigationVisible
                ? `calc(100dvh - ${bottomPosition})`
                : '100dvh',
              bottom: isNavigationVisible ? bottomPosition : 0,
            }}
          >
            <div className="flex flex-col h-full">
              {/* Header - Fixed */}
              <div className="p-3 pb-8 z-10 w-full items-center shrink-0 overflow-inherit color-inherit subpixel-antialiased flex gap-3 justify-between bg-gradient-to-tr from-blue-10 to-red-20/80">
                <div className="flex items-center gap-3 text-white">
                  <Avatar className="size-10">
                    <AvatarImage
                      src={`${assetPrefix}/static/images/new-bot-avt.webp`}
                      alt={'chat bot image'}
                    />
                    <AvatarFallback className="text-primary-foreground overflow-hidden size-14 mt-4 scale-125">
                      Robot
                    </AvatarFallback>
                  </Avatar>
                  <div className="text-secondary-50">
                    <h3 className="font-bold text-sm">Amori</h3>
                    <p className="text-xs font-thin">
                      Solo Meals và Freeship 100%
                    </p>
                  </div>
                </div>
                <div className="flex justify-end text-secondary-50">
                  {isAuthenticated && threadId && (
                    <Button
                      variant="ghost"
                      className="text-secondary-50 hover:bg-transparent"
                      size="icon"
                      onClick={() => {
                        trackEvent('chatbot_event', {
                          button_name: 'chatbot_restart',
                          timestamp: Date.now(),
                          user_id: user?.cunstomerId,
                          thread_id: threadId,
                        });
                        restartThread();
                      }}
                    >
                      <RotateCcw className="size-4" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    className="text-secondary-50 hover:bg-transparent"
                    size="icon"
                    onClick={() => setOpenChange(false)}
                  >
                    <XIcon />
                  </Button>
                </div>
              </div>
              {isAuthenticated && threadId ? (
                <Chat key={threadId} threadId={threadId} className="flex-1" />
              ) : isLoading ? (
                <div className="flex items-center justify-center h-full text-lg text-blue-60">
                  Đang kiểm tra đăng nhập...
                </div>
              ) : (
                <UnAuthen />
              )}
            </div>
          </div>
        </>
      )}
      <LoginDialog
        open={showLoginDialog}
        onOpenChange={setShowLoginDialog}
        location="amori-login"
      />
    </>
  );
}
